import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import PropTypes from 'prop-types';
import { loginRequest } from '../config/msalConfig';
import { tokenStore } from '../api/axiosClient';
import { AZUREKEYS } from '../constant/constant';
import { handleCryptoError, isCryptoAvailable } from '../utils/cryptoUtils.jsx';

const AuthContext = createContext(null);
const USER_KEY = 'auth_user';
const LOGOUT_SIGNAL_KEY = 'auth_logout_signal';

// Helper function to handle logout signal from other tabs
const handleLogoutSignal = (setUser, tokenStore, accounts, instance) => {
  console.log('Logout signal received from another tab');
  
  // Clear local state
  setUser(null);
  tokenStore.setAccess(null);
  
  // Clear MSAL session
  if (accounts.length > 0 && instance) {
    try {
      const allAccounts = instance.getAllAccounts();
      allAccounts.forEach(account => {
        instance.removeAccount(account);
      });
      instance.clearCache();
      console.log('Cleared MSAL session for cross-tab logout');
    } catch (error) {
      console.error('Failed to clear MSAL session:', error);
    }
  }
  
  // Redirect to login
  setTimeout(() => {
    window.location.href = '/login';  // NOSONAR
  }, 100);
};

// Helper function to handle user data sync across tabs
const handleUserDataSync = (newValue, user, setUser, tokenStore) => {
  if (newValue === null && user) {
    // User data cleared in another tab
    console.log('User data cleared in another tab');
    setUser(null);
    tokenStore.setAccess(null);
  } else if (newValue) {
    // User data updated in another tab - sync it
    try {
      const newUserData = JSON.parse(newValue);
      const currentUserString = user ? JSON.stringify(user) : null;
      
      // Only update if the user data is actually different
      if (currentUserString !== newValue) {
        console.log('User data updated in another tab, syncing:', newUserData);
        setUser(newUserData);
      }
    } catch (error) {
      console.error('Failed to parse user data from storage:', error);
    }
  }
};

export function AuthProvider({ children }) {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Check if user is authenticated via Azure AD
  const isAuthenticated = accounts.length > 0 && inProgress === InteractionStatus.None;

  // Debug logging to help troubleshoot authentication issues
  useEffect(() => {
    console.log('Auth Debug Info:', {
      accountsLength: accounts.length,
      inProgress,
      isAuthenticated,
      user,
      interactionStatus: InteractionStatus
    });
  }, [accounts, inProgress, isAuthenticated, user]);

  // Listen for storage changes across browser windows/tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Handle logout signal - dedicated logout event
      if (e.key === LOGOUT_SIGNAL_KEY && e.newValue) {
        handleLogoutSignal(setUser, tokenStore, accounts, instance);
        return; // Exit early to avoid processing USER_KEY changes
      }
      
      // Handle user data changes - sync user data across tabs
      if (e.key === USER_KEY) {
        handleUserDataSync(e.newValue, user, setUser, tokenStore);
      }
    };

    window.addEventListener('storage', handleStorageChange);  // NOSONAR
    return () => window.removeEventListener('storage', handleStorageChange);   // NOSONAR
  }, [instance, accounts, user]);

  useEffect(() => {
    if (accounts.length > 0) {
      const account = accounts[0];
      const userProfile = {
        email: account.username,
        name: account.name || account.username,
        id: account.homeAccountId,
        tenantId: account.tenantId,
        isCognizantUser: account.username?.endsWith("@cognizant.com")
      };
      setUser(userProfile);
      localStorage.setItem(USER_KEY, JSON.stringify(userProfile));
      
      // Get access token and store it
      instance.acquireTokenSilent({
        ...loginRequest,
        account: account
      }).then((response) => {
        tokenStore.setAccess(response.accessToken);
      }).catch((error) => {
        console.error('Silent token acquisition failed:', error);
        // Handle crypto errors in token acquisition
        if (error.message?.includes('crypto_nonexistent')) {
          console.warn('Crypto error during token acquisition, user may need to re-authenticate');
        }
      });
    } else {
      // Only clear user data if there's no user data in localStorage
      // This prevents clearing user data when MSAL accounts get cleared by another tab
      const storedUser = localStorage.getItem(USER_KEY);
      if (storedUser) {
        console.log('No accounts but user data exists in storage, keeping user state');
      } else {
        console.log('No accounts and no stored user data, clearing user state');
        setUser(null);
        tokenStore.setAccess(null);
      }
    }
  }, [accounts, instance]);

  // Azure AD login using redirect (alternative method)
  const loginRedirect = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login redirect failed:', error);
      throw error;
    }
  }, [instance]);

  // Azure AD login using popup
  const login = useCallback(async () => {
    try {
      // Check crypto availability before attempting login
      if (!isCryptoAvailable()) {
        console.warn('Crypto API not available, falling back to redirect login');
        return await loginRedirect();
      }
      
      const response = await instance.loginPopup(loginRequest);
      if (response.account) {
        const userProfile = {
          email: response.account.username,
          name: response.account.name || response.account.username,
          id: response.account.homeAccountId,
          tenantId: response.account.tenantId,
          isCognizantUser: response.account.username?.endsWith("@cognizant.com")
        };
        setUser(userProfile);
        tokenStore.setAccess(response.accessToken);
        return userProfile;
      }
    } catch (error) {
      console.error('Login failed:', error);
      
      // Handle crypto-specific errors with user-friendly messages
      if (error.message?.includes('crypto_nonexistent')) {
        const friendlyMessage = handleCryptoError(error);
        console.error('Crypto error:', friendlyMessage);
        // Try redirect as fallback for crypto issues
        try {
          console.log('Attempting redirect login as fallback...');
          return await loginRedirect();
        } catch (redirectError) {
          console.error('Redirect login fallback failed:', redirectError);
          throw new Error(friendlyMessage);
        }
      }
      
      throw error;
    }
  }, [instance, loginRedirect]);

  // Azure AD logout
  const logout = useCallback(async () => {
    try {
      // Send logout signal to all tabs FIRST
      const logoutSignal = {
        timestamp: Date.now(),
        action: 'logout'
      };
      localStorage.setItem(LOGOUT_SIGNAL_KEY, JSON.stringify(logoutSignal));
      
      // Clear local state
      setUser(null);
      tokenStore.setAccess(null);
      localStorage.removeItem(USER_KEY);
      
      // Clean up the logout signal after a short delay
      setTimeout(() => {
        localStorage.removeItem(LOGOUT_SIGNAL_KEY);
      }, 500);
      
      // Then perform Azure AD logout
      await instance.logoutRedirect({
        postLogoutRedirectUri: AZUREKEYS.REACT_APP_POST_LOGOUT_REDIRECT_URI,
        mainWindowRedirectUri: AZUREKEYS.REACT_APP_POST_LOGOUT_REDIRECT_URI
      });
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: ensure local state is cleared even if logout fails
      setUser(null);
      tokenStore.setAccess(null);
      localStorage.removeItem(USER_KEY);
      
      // Still send logout signal in case of error
      try {
        const logoutSignal = {
          timestamp: Date.now(),
          action: 'logout_error'
        };
        localStorage.setItem(LOGOUT_SIGNAL_KEY, JSON.stringify(logoutSignal));
        setTimeout(() => {
          localStorage.removeItem(LOGOUT_SIGNAL_KEY);
        }, 500);
      } catch (signalError) {
        console.error('Failed to send logout signal:', signalError);
      }
    }
  }, [instance, setUser]);

  // Get fresh access token
  const getAccessToken = useCallback(async () => {
    if (accounts.length > 0) {
      try {
        const response = await instance.acquireTokenSilent({
          ...loginRequest,
          account: accounts[0]
        });
        tokenStore.setAccess(response.accessToken);
        return response.accessToken;
      } catch (error) {
        console.error('Token acquisition failed:', error);
        throw error;
      }
    }
    return null;
  }, [accounts, instance]);

  // Monitor for cleared cookies/localStorage and auto-logout
  useEffect(() => {
    let intervalId;
    
    const checkStorageIntegrity = () => {
      // Only check if user is currently authenticated
      if (user && isAuthenticated) {
        try {
          // Check if localStorage is accessible and contains user data
          const storedUser = localStorage.getItem(USER_KEY);
          const hasStoredToken = tokenStore.getAccess();
          
          // If user is authenticated but storage is cleared, logout
          if (!storedUser || !hasStoredToken) {
            console.warn('Storage cleared detected, logging out user');
            logout();
          }
        } catch (error) {
          // If localStorage is not accessible (cleared/disabled), logout
          console.warn('Storage access error, logging out user:', error);
          logout();
        }
      }
    };
    
    // Check every 5 seconds when user is authenticated
    if (user && isAuthenticated) {
      intervalId = setInterval(checkStorageIntegrity, 5000);
    }
    
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [user, isAuthenticated, logout]);

  const value = useMemo(() => ({ 
    user, 
    isAuthenticated, 
    login, 
    loginRedirect, 
    logout, 
    getAccessToken,
    isLoading: inProgress !== InteractionStatus.None
  }), [user, isAuthenticated, inProgress, login, loginRedirect, logout, getAccessToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// PropTypes validation
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}